import { ComponentProps, FC, ReactNode } from "react";
import { Button as FlowbiteButton } from "flowbite-react";
import { HiOutlinePlus } from "react-icons/hi";
import { btnSecondary, cn } from "@design-system";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "add"
  | "danger"
  | "outline"
  | "success";
export type ButtonSize = "xs" | "sm" | "md" | "lg";
export type IconPosition = "left" | "right" | "only";

type FlowbiteButtonProps = ComponentProps<typeof FlowbiteButton>;

export interface ButtonProps extends Omit<FlowbiteButtonProps, "size"> {
  /**
   * Semantic preset. When provided, the button renders with the shared
   * variant styling. When omitted, the component is a passthrough wrapper over
   * flowbite's `Button` (use `color`, `className`, etc. directly), so raw
   * flowbite / native buttons can converge onto this single component.
   */
  variant?: ButtonVariant;
  icon?: ReactNode;
  iconPosition?: IconPosition;
  /** Preset sizes for `variant` mode; any flowbite size string in passthrough. */
  size?: ButtonSize | (string & {});
}

const getSizeClasses = (size: string) => {
  switch (size) {
    case "xs":
      return "text-xs h-[32px]";
    case "sm":
      return "text-sm h-[38px]";
    case "md":
      return "text-sm h-[42px]";
    case "lg":
      return "text-base h-[48px]";
    default:
      return "text-sm h-[42px]";
  }
};

const ButtonComponent: FC<ButtonProps> = (props) => {
  const {
    variant,
    icon,
    iconPosition = "left",
    size = "md",
    className = "",
    children,
    onClick,
    disabled,
    type = "button",
    ...rest
  } = props;

  // Passthrough mode: behave exactly like a themed flowbite Button.
  if (!variant) {
    return (
      <FlowbiteButton
        size={size as any}
        className={className}
        onClick={onClick}
        disabled={disabled}
        type={type}
        {...rest}
      >
        {children}
      </FlowbiteButton>
    );
  }

  const getVariantProps = () => {
    switch (variant) {
      case "primary":
        return {
          color: "primary",
          className: `${getSizeClasses(size)} ${className}`,
        };
      case "secondary":
        return {
          color: "light",
          className: cn(
            btnSecondary,
            getSizeClasses(size),
            className as string,
          ),
        };
      case "add":
        return {
          color: "light",
          className: cn(
            btnSecondary,
            getSizeClasses(size),
            className as string,
          ),
        };
      case "danger":
        return {
          color: "failure",
          className: `${getSizeClasses(size)} ${className}`,
        };
      case "outline":
        return {
          color: "light",
          className: cn(
            btnSecondary,
            getSizeClasses(size),
            className as string,
          ),
        };
      case "success":
        return {
          color: "custom",
          className: cn(
            "bg-success hover:bg-lgb-green-500 text-text-inverse",
            getSizeClasses(size),
            className as string,
          ),
        };
      default:
        return {
          color: "primary",
          className: `${getSizeClasses(size)} ${className}`,
        };
    }
  };

  const variantProps = getVariantProps();

  const getIcon = () => {
    if (icon) {
      return icon;
    }

    if (variant === "add") {
      return <HiOutlinePlus className="h-5 w-5" />;
    }

    return null;
  };

  const renderButtonContent = () => {
    const iconElement = getIcon();

    if (iconPosition === "only" && iconElement) {
      return iconElement;
    }

    if (iconPosition === "left" && iconElement) {
      return (
        <>
          <span className="mr-1">{iconElement}</span>
          {children}
        </>
      );
    }

    if (iconPosition === "right" && iconElement) {
      return (
        <>
          {children}
          <span className="ml-1">{iconElement}</span>
        </>
      );
    }

    return children;
  };

  const getPaddingClasses = () => {
    const hasIcon = getIcon() !== null;
    const isIconOnly = iconPosition === "only" && hasIcon;

    if (isIconOnly) {
      return "!px-1.5 !py-0";
    }

    switch (size) {
      case "xs":
        return "!px-1.5 !py-0";
      case "sm":
        return "!px-2 !py-0";
      case "md":
        return "!px-2 !py-0";
      case "lg":
        return "!px-2.5 !py-0";
      default:
        return "!px-2 !py-0";
    }
  };

  return (
    <FlowbiteButton
      {...rest}
      color={variantProps.color as any}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center whitespace-nowrap focus:ring-0 focus:outline-none w-auto ${getPaddingClasses()} ${variantProps.className}`}
      type={type}
      pill={false}
    >
      {renderButtonContent()}
    </FlowbiteButton>
  );
};

/**
 * Single canonical button for the app. Use `variant` for the semantic presets,
 * or omit it to passthrough to the themed flowbite Button (`color`, `size`,
 * `href`, `className`, etc.). `Button.Group` is re-exported for grouped layouts.
 */
export const Button = Object.assign(ButtonComponent, {
  Group: FlowbiteButton.Group,
});
